import React, { FunctionComponent, useState, useEffect } from 'react';
import styled from 'styled-components';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { MockDataService, ToolOutput } from 'src/services/mockDataService';
import { OrchestrationService, OrchestrationResponse, ToolResult } from 'src/services/orchestrationService';
import ResponseCard from '../ResponseCard';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled(Text)`
  color: ${Colors.extra.black};
`;

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FilterButton = styled(Button)<{ active?: boolean }>`
  background-color: ${props => props.active ? Colors.theme.primary : Colors.extra.light_gray};
  color: ${props => props.active ? Colors.extra.white : Colors.extra.black};
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? Colors.theme.primary : Colors.extra.dark_gray};
    color: ${Colors.extra.white};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled(Card)`
  padding: 16px;
  text-align: center;
`;

const StatValue = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: ${Colors.theme.primary};
  margin-bottom: 4px;
`;

const SuccessStatValue = styled(StatValue)`
  color: #10b981;
`;

const ErrorStatValue = styled(StatValue)`
  color: #ef4444;
`;

const PendingStatValue = styled(StatValue)`
  color: #f59e0b;
`;

const StatLabel = styled(Text)`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
`;

const ToolOutputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Colors.extra.dark_gray};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Colors.extra.dark_gray};
`;

const MultiToolDisplay: FunctionComponent = () => {
  const [toolOutputs, setToolOutputs] = useState<ToolOutput[]>([]);
  const [filteredOutputs, setFilteredOutputs] = useState<ToolOutput[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [orchestrationData, setOrchestrationData] = useState<OrchestrationResponse | null>(null);
  const [useBackend, setUseBackend] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      try {
        if (useBackend) {
          // Try to load from backend orchestration service
          const sessionHistory = await OrchestrationService.getSessionHistory();
          if (sessionHistory && sessionHistory.length > 0) {
            // Convert trace data to tool outputs format
            const convertedOutputs = sessionHistory.map((trace, index) => ({
              id: `trace_${index}`,
              tool: trace.tools_used.join(', '),
              status: trace.status as 'success' | 'error' | 'pending',
              result: {
                summary: `Session ${trace.session_id}: ${trace.intent}`,
                timestamp: trace.timestamp
              },
              timestamp: trace.timestamp
            }));
            setToolOutputs(convertedOutputs);
            setFilteredOutputs(convertedOutputs);
          } else {
            // Fallback to mock data
            await MockDataService.simulateApiCall(null, 1000);
            const outputs = MockDataService.getToolOutputs();
            setToolOutputs(outputs);
            setFilteredOutputs(outputs);
          }
        } else {
          // Use mock data
          await MockDataService.simulateApiCall(null, 1000);
          const outputs = MockDataService.getToolOutputs();
          setToolOutputs(outputs);
          setFilteredOutputs(outputs);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        // Fallback to mock data on error
        const outputs = MockDataService.getToolOutputs();
        setToolOutputs(outputs);
        setFilteredOutputs(outputs);
      }
      
      setLoading(false);
    };

    loadData();
  }, [useBackend]);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredOutputs(toolOutputs);
    } else {
      setFilteredOutputs(toolOutputs.filter(output => output.status === activeFilter));
    }
  }, [activeFilter, toolOutputs]);

  const handleRefresh = async () => {
    setLoading(true);
    
    try {
      if (useBackend) {
        // Refresh from backend
        const sessionHistory = await OrchestrationService.getSessionHistory();
        if (sessionHistory && sessionHistory.length > 0) {
          const convertedOutputs = sessionHistory.map((trace, index) => ({
            id: `trace_${index}`,
            tool: trace.tools_used.join(', '),
            status: trace.status as 'success' | 'error' | 'pending',
            result: {
              summary: `Session ${trace.session_id}: ${trace.intent}`,
              timestamp: trace.timestamp
            },
            timestamp: trace.timestamp
          }));
          setToolOutputs(convertedOutputs);
          setFilteredOutputs(convertedOutputs);
        } else {
          // Fallback to mock data
          const outputs = MockDataService.getToolOutputs();
          setToolOutputs(outputs);
          setFilteredOutputs(outputs);
        }
      } else {
        // Refresh mock data
        await MockDataService.simulateApiCall(null, 500);
        const outputs = MockDataService.getToolOutputs();
        setToolOutputs(outputs);
        setFilteredOutputs(outputs);
      }
    } catch (error) {
      console.error('Failed to refresh data:', error);
      // Fallback to mock data on error
      const outputs = MockDataService.getToolOutputs();
      setToolOutputs(outputs);
      setFilteredOutputs(outputs);
    }
    
    setLoading(false);
  };

  const getStats = () => {
    const total = toolOutputs.length;
    const success = toolOutputs.filter(o => o.status === 'success').length;
    const error = toolOutputs.filter(o => o.status === 'error').length;
    const pending = toolOutputs.filter(o => o.status === 'pending').length;
    
    return { total, success, error, pending };
  };

  const stats = getStats();

  if (loading) {
    return (
      <DashboardWrapper>
        <Container>
          <LoadingState>
            <Text fontSize={Size.Medium}>Loading tool outputs...</Text>
          </LoadingState>
        </Container>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <Container>
        <Header>
          <div>
            <Title fontSize={Size.Large} fontWeight="600">
              Multi-Tool Display
            </Title>
            <Text fontSize={Size.Small} color={Colors.extra.dark_gray} marginTop={4}>
              View and analyze outputs from multiple AI tools
            </Text>
          </div>
          <Controls>
            <FilterButton
              active={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            >
              All ({stats.total})
            </FilterButton>
            <FilterButton
              active={activeFilter === 'success'}
              onClick={() => setActiveFilter('success')}
            >
              Success ({stats.success})
            </FilterButton>
            <FilterButton
              active={activeFilter === 'error'}
              onClick={() => setActiveFilter('error')}
            >
              Error ({stats.error})
            </FilterButton>
            <FilterButton
              active={activeFilter === 'pending'}
              onClick={() => setActiveFilter('pending')}
            >
              Pending ({stats.pending})
            </FilterButton>
            <FilterButton
              active={useBackend}
              onClick={() => setUseBackend(!useBackend)}
            >
              {useBackend ? 'Backend Data' : 'Mock Data'}
            </FilterButton>
            <Button onClick={handleRefresh}>
              Refresh
            </Button>
          </Controls>
        </Header>

        <StatsContainer>
          <StatCard>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Outputs</StatLabel>
          </StatCard>
          <StatCard>
            <SuccessStatValue>{stats.success}</SuccessStatValue>
            <StatLabel>Successful</StatLabel>
          </StatCard>
          <StatCard>
            <ErrorStatValue>{stats.error}</ErrorStatValue>
            <StatLabel>Errors</StatLabel>
          </StatCard>
          <StatCard>
            <PendingStatValue>{stats.pending}</PendingStatValue>
            <StatLabel>Pending</StatLabel>
          </StatCard>
        </StatsContainer>

        <ToolOutputsContainer>
          {filteredOutputs.length === 0 ? (
            <EmptyState>
              <Text fontSize={Size.Medium} marginBottom={8}>
                No tool outputs found
              </Text>
              <Text fontSize={Size.Small} color={Colors.extra.dark_gray}>
                {activeFilter === 'all' 
                  ? 'No tool outputs available' 
                  : `No ${activeFilter} outputs found`}
              </Text>
            </EmptyState>
          ) : (
            filteredOutputs.map((output) => (
              <ResponseCard key={output.id} toolOutput={output} />
            ))
          )}
        </ToolOutputsContainer>
      </Container>
    </DashboardWrapper>
  );
};

export default MultiToolDisplay;

